import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster, toast } from 'sonner'
import { registerSW } from 'virtual:pwa-register'

import App from './App'
import SyncController from './components/SyncController'
import { ApiHttpError } from './lib/api'
import { markLoggedOut } from './lib/auth'
import './index.css'

// Bez `onOfflineReady` i jego toastu o pracy bez sieci — start i koniec pracy
// wymagają potwierdzenia z serwera, więc taka obietnica byłaby nieprawdą.
const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    toast('Dostępna nowa wersja', {
      description: 'Kliknij Odśwież, żeby załadować najnowszą wersję aplikacji.',
      action: {
        label: 'Odśwież',
        onClick: () => {
          void updateSW(true)
        },
      },
      duration: Infinity,
    })
  },
})

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({
    // 401 z dowolnego zapytania = sesja na serwerze już nie istnieje. Bez tego
    // ekran dalej pokazywał „zalogowany" ze starych danych, aż do kliknięcia.
    onError: (err) => {
      if (err instanceof ApiHttpError && err.status === 401) {
        markLoggedOut(queryClient)
      }
    },
  }),
  defaultOptions: {
    queries: {
      // Czasy pracy zmieniają się rzadko — krótkie TTL, brak refetch on focus
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('Brak elementu #root w index.html')

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <SyncController />
      <BrowserRouter>
        <App />
        <Toaster
          position="top-right"
          richColors
          closeButton
          toastOptions={{
            classNames: {
              toast:
                'rounded-card border border-border-subtle bg-bg-elevated text-text-primary',
            },
          }}
        />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
